// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import * as Slice from "../slice"
import {
  CreateTimesheetFieldRequest,
  ListTimesheetFieldOptionsRequest,
  TimesheetField,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type CreateTimesheetFieldRpc = (request: CreateTimesheetFieldRequest) => Observable<TimesheetField>

export const createTimesheetField = (createTimesheetFieldRpc: CreateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        createTimesheetFieldRpc(payload.request).pipe(
          mergeMap(response => {
            const timesheetField = response.toObject()
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return concat([
              Slice.addNewFieldId(timesheetField.id),
              TimelineSlice.setTimesheetField(timesheetField),
              Actions.listTimesheetFieldOptions(
                new ListTimesheetFieldOptionsRequest()
                  .setTimesheetFieldId(timesheetField.id)
                  .setSiteId(timesheetField.siteId)
              ),
            ])
          }),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_create_timesheet_field_permission_denied",
          message: "Failed to create timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_create_timesheet_field_failed",
      message: "Failed to create timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const createTimesheetFieldRpc = (request: CreateTimesheetFieldRequest): Observable<TimesheetField> =>
  grpc(() => timesheetFieldClient.createTimesheetField(request, null))

export default createTimesheetField(createTimesheetFieldRpc)
